import { useRef, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Swiper as SwiperClass } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/virtual';

import styles from './SingleView.module.css';
import { SingleViewContext } from './SingleView.context';
import { IssueContext } from '../../Issue.context';
import { ReplicaContext } from '../Replica.context';
import { AppContext } from '../../../../App.context';

import { useOutsideClick } from '../../../../hooks/use-outside-click';
import { useKeyPressShortcut } from '../../../../hooks/use-shortcut';

import { Icon } from '../../../../components/icon/Icon';
import { NavigationBar } from '../../../../components/navigation-bar/NavigationBar';
import { Page } from '../../../../components/article/Replica';
import { Hotspot } from '../../../../components/article/Replica.context';

import { Menu, ProfileMenu } from '../../../../components/menu/Menu';
import { Button } from '../../menu-button/MenuButton';
import {
	NavbarIconLogo,
	fixInvalidInPages,
	clone,
} from '../../../../constants';
import { useBackUrl } from '../../../../App.hooks';
import config from '../../../../config';
import { TrackingData } from '../../../../tracking/Tracking.events';
import { TrackingContext } from '../../../../tracking/Tracking.context';
import { FileSliders, UserRound } from 'lucide-react';

export const SingleView = (props: SingleViewProps) => {
	const { issue } = props;
	const { sendEvent } = useContext(TrackingContext);
	const { isSidebarOpen } = useContext(ReplicaContext);
	const { currentArticle, language } = useContext(IssueContext);
	const [swiper, setSwiper] = useState<SwiperClass>();
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isProfileMenuOpen, setIsProfileMenuOpen] = useState<boolean>(false);
	const [index, setIndex] = useState<number | null>(null);
	const classNames = [styles['issue']];
	if (isSidebarOpen) {
		classNames.push(styles['sidebar-open']);
	} else {
		classNames.push(styles['sidebar-close']);
	}

	const pages: Array<any> = issue.articles
		.reduce((acc: Array<any>, article: any) => {
			const { replica } = article;
			if (!replica) {
				return acc;
			}
			for (const item of replica) {
				item.articleId = article.id;
				acc.push(item);
			}
			return acc;
		}, [])
		.sort((a: any, b: any) => a.index - b.index)
		.reduce(removeDuplicatePages, { pagesSet: new Set(), pages: [] }).pages;

	useEffect(() => {
		if (isLoaded) {
			return;
		}

		if (!currentArticle) {
			return;
		}

		for (let i = 0; i < pages.length; i++) {
			const page = pages[i];
			if (page.articleId === currentArticle.id) {
				setIndex(i);
				setIsLoaded(true);
				return;
			}
		}
		setIndex(0);
		setIsLoaded(true);
	}, [pages, isLoaded, currentArticle]);

	useTrackPageArticle({
		pages,
		index,
		isLoaded,
	});

	useEffect(() => {
		if (!swiper) {
			return;
		}

		if (index === null) {
			return;
		}

		if (swiper.destroyed) {
			return;
		}

		swiper.slideTo(index, 750);
	}, [swiper, index]);

	const onNextArticleClick = () => {
		if (index === null) {
			return;
		}
		if (index + 1 >= pages.length) {
			return;
		}
		setIndex(index + 1);
	};

	const onPrevArticleClick = () => {
		if (index === null) {
			return;
		}

		if (index === 0) {
			return;
		}

		setIndex(index - 1);
	};

	useKeyPressShortcut(['ArrowRight'], onNextArticleClick);
	useKeyPressShortcut(['ArrowLeft'], onPrevArticleClick);

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	if (!isLoaded || index === null) {
		return null;
	}

	return (
		<SingleViewContext.Provider
			value={{
				swiper,
				setSwiper,
				index,
				setIndex,
				pages,
				onNextArticleClick,
				onPrevArticleClick,
				toggleMenu,
				isProfileMenuOpen,
				setIsProfileMenuOpen,
			}}
		>
			<div className={classNames.join(' ')}>
				<Topbar />
				<Sidebar />
				<Container />
			</div>
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				{config?.app?.menu?.externalUrl?.enabled ? (
					<Button.WebsiteButton
						url={config?.app?.menu?.externalUrl.url}
						title={config?.app?.menu?.externalUrl.title}
					/>
				) : (
					<></>
				)}
				<Button.ShareArticle
					article={currentArticle}
					language={language}
					onShare={(article, method = 'os') => {
						const eventData: TrackingData = {
							method,
							contentType: 'article',
							article: {
								id: parseInt(`${article.id}`, 10),
								ArticleID: parseInt(`${article.ArticleID}`, 10),
							},
						};
						sendEvent({
							type: 'SHARE',
							data: eventData,
						});
					}}
				/>
				<Button.Library />
				<Button.Responsive />
				<Button.DoublePage />
				<Button.Links links={config?.app?.menu?.links} />
				<Button.About />
			</Menu>
			<ProfileMenu
				open={isProfileMenuOpen}
				setOpen={setIsProfileMenuOpen}
			/>
		</SingleViewContext.Provider>
	);
};

interface SingleViewProps {
	issue: any;
	onChangeResponsive: Function;
}

function removeDuplicatePages(
	acc: { pagesSet: Set<string>; pages: Array<any> },
	page: any,
) {
	const { pagesSet, pages } = acc;
	const { name } = page;
	if (pagesSet.has(name)) {
		return acc;
	}

	pagesSet.add(name);
	pages.push(page);

	return {
		pagesSet,
		pages,
	};
}

/**
 * React hook that tracks what is the article of the page
 *
 */
function useTrackPageArticle(props: TrackPageArticleArgs) {
	const { setCurrentArticle, currentArticle, articlesMap } =
		useContext(IssueContext);
	const { pages, index, isLoaded } = props;
	useEffect(() => {
		if (!articlesMap) {
			return;
		}

		if (index === null) {
			return;
		}

		if (!pages.length) {
			return;
		}

		if (!isLoaded) {
			return;
		}

		const page = pages[index];
		const nextArticle = articlesMap.get(page.articleId);
		if (nextArticle && nextArticle.id !== currentArticle.id) {
			setCurrentArticle(clone(nextArticle));
		}
	}, [pages, articlesMap, index]);
}

interface TrackPageArticleArgs {
	isLoaded: boolean;
	pages: Array<any>;
	index: number | null;
}

function Topbar() {
	const { isSidebarOpen, setIsSidebarOpen } = useContext(ReplicaContext);

	return (
		<nav className={styles['topbar']}>
			<div>
				<BackButton />
				<button
					className="tooltip down"
					data-text={isSidebarOpen ? 'Close' : 'Open'}
					type="button"
					onClick={() => {
						setIsSidebarOpen(!isSidebarOpen);
					}}
				>
					<Icon
						id={'sidebar-btn'}
						name={'sidebar'}
						color={
							isSidebarOpen
								? 'var(--app-accent-color)'
								: '#c3c3c3'
						}
					/>
				</button>
			</div>
			<div className={styles['logo']}>
				<NavbarIconLogo />
			</div>
			<div>
				<MenuButton />
				<ProfileButton />
			</div>
		</nav>
	);
}

function BackButton() {
	const url = useBackUrl();
	const navigate = useNavigate();
	const { issue } = useContext(IssueContext);
	if (!issue) {
		return null;
	}

	return (
		<button
			id="back-button"
			className="tooltip down"
			data-text="Back"
			onClick={() => {
				navigate(url);
			}}
			type="button"
			title="Back"
		>
			<Icon name={'arrow-left'} color={'#c3c3c3'} />
		</button>
	);
}

function ProfileButton() {
	const { setIsProfileMenuOpen } = useContext(SingleViewContext);
	if (!config?.app?.auth?.enabled) {
		return null;
	}
	if (config?.app?.auth?.type !== 'cognito') {
		return null;
	}
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Profile'}
			onClick={() => {
				setIsProfileMenuOpen(true);
			}}
		>
			<UserRound color={'#c3c3c3'} size={24} />
		</button>
	);
}

function MenuButton() {
	const { toggleMenu } = useContext(SingleViewContext);
	return (
		<button
			type="button"
			className="tooltip down"
			data-text={'Actions'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<FileSliders color="#c3c3c3" />
		</button>
	);
}

function Sidebar() {
	const { isSidebarOpen, setIsSidebarOpen } = useContext(ReplicaContext);
	const ref = useRef(null);
	useOutsideClick(
		ref,
		() => {
			if (isSidebarOpen) {
				setIsSidebarOpen(false);
			}
		},
		'sidebar-btn',
	);
	return (
		<aside ref={ref} className={styles['sidebar']}>
			<span></span>
			<Toc />
		</aside>
	);
}

function Toc() {
	const { pages, index } = useContext(SingleViewContext);
	return (
		<ul className={styles['toc']}>
			{pages.map((replicaPage, pos) => {
				const { page, name, image } = replicaPage;
				return (
					<TocItem
						key={`list-${page}`}
						thumbnail={fixInvalidInPages(image)}
						title={name}
						isActive={pos === index}
						index={pos}
						page={page}
					/>
				);
			})}
		</ul>
	);
}

function TocItem({ thumbnail, isActive, index, page }: TocItemProps) {
	const { device } = useContext(AppContext);
	const { setIsSidebarOpen } = useContext(ReplicaContext);
	const { setIndex } = useContext(SingleViewContext);
	const contentProtection = config?.app?.contentProtection;
	const onClick = () => {
		setIndex(index);
		if (device?.type === 'mobile') {
			setIsSidebarOpen(false);
		}
	};
	const onKeyDown = (event: React.KeyboardEvent<HTMLLIElement>) => {
		if (event.key === 'Enter') {
			setIndex(index);
			if (device?.type === 'mobile') {
				setIsSidebarOpen(false);
			}
		}
	};

	return (
		<li
			role="button"
			className={isActive ? styles['active'] : ''}
			onClick={onClick}
			onKeyDown={onKeyDown}
			tabIndex={index}
		>
			<div>
				{contentProtection ? (
					<img
						title={`Page: ${page}`}
						src={thumbnail}
						alt={`Page: ${page}`}
						className="content-protection"
						onContextMenu={(e) => e.preventDefault()}
					/>
				) : (
					<img
						title={`Page: ${page}`}
						src={thumbnail}
						alt={`Page: ${page}`}
					/>
				)}
			</div>
			<span>
				<small>{page}</small>
			</span>
		</li>
	);
}

interface TocItemProps {
	title: string;
	thumbnail: string;
	isActive: boolean;
	index: number;
	page: number;
}

function Container() {
	const { onNextArticleClick, index, onPrevArticleClick, pages } =
		useContext(SingleViewContext);
	const classNames = [styles['content']];
	return (
		<div className={classNames.join(' ')}>
			<SinglePages />
			{pages.length > 1 ? (
				<NavigationBar
					classNames={[styles['navigation-bar']]}
					onNextClick={onNextArticleClick}
					onPrevClick={onPrevArticleClick}
					disable={{
						left: index === 0,
						right: index === pages.length - 1,
					}}
				/>
			) : null}
		</div>
	);
}

export const SinglePages = () => {
	const { index, pages, setIndex } = useContext(SingleViewContext);
	const { setSwiper } = useContext(SingleViewContext);
	const onClickHotspot = (hotspot: Hotspot) => {
		const { url } = hotspot;
		open(url);
	};
	return (
		<div className={styles['single-page']}>
			<Swiper
				modules={[Virtual]}
				updateOnWindowResize={true}
				initialSlide={index}
				slidesPerView={1}
				spaceBetween={30}
				direction="horizontal"
				shortSwipes={true}
				preventClicks={true}
				scrollbar={{
					draggable: false,
				}}
				onSwiper={(s: SwiperClass) => {
					setSwiper(s);
				}}
				onTransitionEnd={(s: SwiperClass) => {
					const { activeIndex } = s;
					setIndex(activeIndex);
				}}
				virtual
			>
				{pages.map((page, i) => (
					<SwiperSlide key={page.page} virtualIndex={i}>
						<div className={styles['page']}>
							<Page
								name={page.name}
								image={page.image}
								page={i}
								hotspots={page.hotspots || []}
								onClickHotspot={onClickHotspot}
							/>
						</div>
					</SwiperSlide>
				))}
			</Swiper>
		</div>
	);
};
